// This module defines a CRUD-style screen for a Route-Lane relationship record.
// The screen combines a metadata-driven form (DynamicForm) with a map preview
// (RoutePreviewTable) that visualizes all lanes for the selected Route and
// highlights the currently selected Lane.
//
// The component is designed to operate in tabbed navigation contexts where each
// tab maintains its own draft and view state. State is persisted and restored
// through the shared M_State snapshot store so that navigation between tabs does
// not discard user input or fetched data.

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
  MHeader,
  MButton,
  LoadingContext,
  M_StateActions,
  NavigateContext,
} from 'src/M_Components';

import { BufferService } from 'src/M_Components/M_Utils/buffer.service';
import { isFailurePayload, apiMessage } from 'src/components/utils/apiResponse';
import DynamicForm from 'src/components/Forms/DynamicForm';
import RoutePreviewTable from 'src/components/routePreview/routePreviewTable';
import '../pages.css';

/* --------------------------------------------------------------------------------------------------------------
 * RouteLaneConfigCrud
 *
 * Purpose
 * - Display and edit a Route-Lane relationship record using DynamicForm metadata.
 * - Hydrate DynamicForm schema from backend Options to keep UI aligned with server definitions.
 * - Persist per-tab drafts and view state in the shared M_State snapshot store.
 * - Render a map preview of lanes linked to the selected Route and highlight the selected Lane.
 *
 * Behavior summary
 * - Global loader: used for page-level operations (Options, Details, Save, tab restore).
 * - Local map busy overlay: used for map-related fetch and geometry preparation to avoid loader flicker.
 * - The relationship operational availability is driven by IsActive (not Inactive / soft delete).
 * -------------------------------------------------------------------------------------------------------------- */

/* ==============================================================================================================
 * Map constants
 * ============================================================================================================ */

/**
 * RGBA highlight color used to draw the currently selected lane on top of other lanes.
 * The value is passed through to RoutePreviewTable as highlightColor.
 */
const COLOR_CURRENT = [212, 175, 55, 255];

/* ==============================================================================================================
 * Component
 * ============================================================================================================ */

/**
 * RouteLaneConfigCrud renders the Route-Lane relationship CRUD screen.
 *
 * Responsibilities:
 * - Retrieve Options metadata used as DynamicForm schema.
 * - Load relationship details for Edit/View modes when composite keys exist.
 * - Keep the current tab state persisted in the shared snapshot store.
 * - Provide toolbar actions for Refresh and Save.
 * - Display a map preview for the current Route and highlight the current Lane.
 *
 * @param {{handleTabClose:function, toastMessages:function}} props Component props
 * @returns {JSX.Element} CRUD screen layout
 */
export default function RouteLaneConfigCrud({ handleTabClose, toastMessages }) {
  /**
   * Redux dispatch is used to persist and clear values in the global state store.
   */
  const dispatch = useDispatch();

  /**
   * The shared snapshot store contains per-tab serialized state and one-shot navigation values.
   */
  const M_State = useSelector((state) => state.StateKeyValue);

  /**
   * The currently active tab identifier.
   * This value is used to fetch and persist tab-specific snapshots.
   */
  const selectedTab = useSelector((s) =>
    s.StateKeyValue.find((o) => o.key === 'selectedTab')?.value
  );

  /**
   * Translation helpers.
   * - t: localizes labels and messages
   * - i18n: exposes active language for reacting to language changes
   */
  const { t, i18n } = useTranslation();

  /**
   * Page-wide loading context.
   * The global loader is used for Options, Details, Save, and snapshot restore flows.
   */
  const ctx = useContext(LoadingContext);

  /**
   * Navigation context provides imperative tab naming support.
   */
  const { renameTab } = useContext(NavigateContext);

  /**
   * One-shot navigation values stored in the global snapshot store.
   * These values are meant to seed the current tab state during boot.
   */
  const globalOperation = M_State.find((obj) => obj.key === 'operation')?.value;
  const globalLine = M_State.find((obj) => obj.key === 'RouteLaneLine')?.value;

  /**
   * Per-tab state: current tab identifier.
   * This value aligns the component to the active tab and selects the correct snapshot entry.
   */
  const [id, setId] = useState(
    M_State.find((obj) => obj.key === 'selectedTab')?.value
  );

  /**
   * Per-tab state: operation mode.
   * Supported modes are typically Create, Edit, and View.
   */
  const [operation, setOperation] = useState(globalOperation ?? 'Error');

  /**
   * Per-tab state: navigation line containing composite keys.
   * Expected to include RouteId and LaneId (and optionally Route/Lane display names).
   */
  const [sessionLine, setSessionLine] = useState(globalLine ?? {});

  /**
   * Per-tab state: the hydrated entity used as the DynamicForm data source.
   * This object is also used to build user-friendly tab labels.
   */
  const [laneInfo, setLaneInfo] = useState({});

  /**
   * Per-tab state: first-render guard used to avoid unnecessary refetch on initial mount.
   */
  const [isFirst, setIsFirst] = useState(true);

  /**
   * View mode disables save actions and treats form values as read-only.
   */
  const isViewMode = (operation || '').toLowerCase() === 'view';

  /**
   * DynamicForm imperative API reference.
   *
   * The reference is expected to expose:
   * - submit(): triggers validation and calls onSubmit
   * - getValues(): returns current React Hook Form values
   * - reset(): replaces form values based on DynamicForm configuration
   */
  const formRef = useRef(null);

  /* ------------------------------------------------------------------------------------------------------------
   * Data services
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Service used to retrieve Options metadata and perform non-composite RouteLaneRel operations.
   * Options provide the schema used by DynamicForm for rendering.
   */
  const svcRouteLane = useMemo(
    () => new BufferService('RouteLaneRels', ctx.DEFAULTURL),
    [ctx.DEFAULTURL]
  );

  /**
   * Creates a service for the composite Route-Lane relationship endpoint.
   *
   * Purpose:
   * - Supports get/put operations where the relationship is identified by RouteId and LaneId.
   *
   * @param {number|string} routeId Route identifier
   * @param {number|string} laneId Lane identifier
   * @returns {BufferService} BufferService bound to the composite endpoint
   */
  const makeCompositeService = useCallback(
    (routeId, laneId) =>
      new BufferService(
        `RouteLaneRels/Route/${routeId}/Lane/${laneId}`,
        ctx.DEFAULTURL
      ),
    [ctx.DEFAULTURL]
  );

  /* ------------------------------------------------------------------------------------------------------------
   * DynamicForm schema
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Stores the Options metadata returned by the backend.
   * This array drives DynamicForm fields, types, and validation behavior.
   */
  const [schema, setSchema] = useState([]);

  /**
   * Snapshot keys stored under the tab identifier in the shared snapshot store.
   * These keys define the persisted shape and restore order.
   */
  const persistKeys = useMemo(
    () => ['laneInfo', 'isFirst', 'operation', 'sessionLine', 'df.schema'],
    []
  );

  /* ------------------------------------------------------------------------------------------------------------
   * Tab label
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Updates the tab label based on:
   * - operation mode (Create/Edit/View)
   * - available Route/Lane names from laneInfo or sessionLine
   *
   * Purpose:
   * - Improves tab readability when multiple relationship tabs are open.
   * - Keeps titles aligned with translated base labels.
   */
  useEffect(() => {
    if (!selectedTab || !operation) return;

    // Selects the translation key for the current operation mode.
    const key =
      operation === 'Create'
        ? 'LabelNewRouteLaneRel'
        : operation === 'Edit'
          ? 'LabelEditRouteLane'
          : 'LabelViewRouteLane';

    // Attempts to compose a human-readable label using route and lane names.
    const routeName = laneInfo?.Route ?? sessionLine?.Route ?? '';
    const laneName = laneInfo?.Lane ?? sessionLine?.Lane ?? '';
    const composed = routeName || laneName ? `${routeName} -> ${laneName}` : '';

    // Builds the final label and applies it to the selected tab.
    const base = t(key);
    const label =
      operation === 'Create' || !composed ? base : `${base} "${composed}"`;

    renameTab(String(selectedTab), label);
  }, [
    selectedTab,
    operation,
    laneInfo?.Route,
    laneInfo?.Lane,
    sessionLine?.Route,
    sessionLine?.Lane,
    t,
    renameTab,
  ]);

  /* ------------------------------------------------------------------------------------------------------------
   * Boot sequence
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Restores per-tab snapshot state and consumes one-shot navigation values.
   *
   * Responsibilities:
   * - Restore saved state for the current tab identifier when available.
   * - Seed operation and sessionLine from one-shot global values when present.
   * - Remove one-shot global values from the shared store after consumption.
   * - Run the bootstrap chain to fetch Options and, when applicable, relationship details.
   *
   * Notes:
   * - The bootstrap call receives seed values directly to avoid dependency on state update timing.
   */
  useEffect(() => {
    // Restores the snapshot associated with the current tab identifier.
    const sessionForThisTab = M_State.find((o) => o.key === id)?.value || [];
    const pick = (k) => sessionForThisTab.find((x) => x.key === k)?.value;

    // Restored snapshot fields, when present.
    const snapInfo = pick('laneInfo');
    const snapIF = pick('isFirst');
    const snapOP = pick('operation');
    const snapSL = pick('sessionLine');
    const snapSchema = pick('df.schema');

    // Applies restored values to local state.
    if (snapInfo) setLaneInfo(snapInfo);
    if (Array.isArray(snapSchema)) setSchema(snapSchema);

    // Restores or initializes the first-render guard.
    if (snapIF !== undefined) setIsFirst(snapIF);
    else if (isFirst) setIsFirst(false);

    // Seeds operation and line from one-shot navigation values when present.
    const seedOperation =
      globalOperation !== undefined ? globalOperation : snapOP;
    const seedLine = globalLine !== undefined ? globalLine : snapSL;

    if (seedOperation !== undefined) setOperation(seedOperation);
    if (seedLine) setSessionLine(seedLine);

    // Removes one-shot values so they do not affect other tabs or future restores.
    if (globalOperation !== undefined) {
      dispatch(M_StateActions.removeM_State({ key: 'operation' }));
    }
    if (globalLine !== undefined) {
      dispatch(M_StateActions.removeM_State({ key: 'RouteLaneLine' }));
    }

    // Executes the hydration chain using seed values when present.
    bootstrap(seedOperation ?? operation, seedLine ?? sessionLine);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ------------------------------------------------------------------------------------------------------------
   * Snapshot persistence
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Persists the current tab state in the shared snapshot store.
   *
   * Persistence behavior:
   * - View mode persists laneInfo exactly as loaded.
   * - Create/Edit modes merge the current form draft values into laneInfo so navigation does not discard edits.
   *
   * Notes:
   * - getValues() is used to capture drafts without forcing a submit.
   * - The snapshot payload shape is aligned to persistKeys order.
   */
  useEffect(() => {
    if (!id) return;

    // Reads current form values when available to preserve drafts.
    const currentFormValues = formRef.current?.getValues?.() || {};

    // View mode persists server data as-is; edit modes persist a merged draft view.
    const infoToPersist =
      (operation || '').toLowerCase() === 'view'
        ? laneInfo
        : { ...laneInfo, ...currentFormValues };

    // Builds the snapshot payload in a stable key/value shape.
    const values = [infoToPersist, isFirst, operation, sessionLine, schema];
    const payload = persistKeys.map((k, i) => ({ key: k, value: values[i] }));

    dispatch(M_StateActions.addM_State({ key: id, value: payload }));
  }, [
    id,
    laneInfo,
    isFirst,
    operation,
    sessionLine,
    schema,
    dispatch,
    persistKeys,
  ]);

  /* ------------------------------------------------------------------------------------------------------------
   * Tab change handling
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Tracks selectedTab changes and restores the matching snapshot.
   *
   * Purpose:
   * - Ensures the component state reflects the active tab.
   * - Prevents stale data from a previous tab from being displayed.
   */
  useEffect(() => {
    const newId = M_State.find((obj) => obj.key === 'selectedTab')?.value;
    if (newId && newId !== id) {
      setId(newId);
      getSessionStorageData(newId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [M_State]);

  /**
   * Restores the persisted snapshot for a given tab identifier.
   *
   * Behavior:
   * - When a snapshot exists, it is applied to local state and the global loader is stopped.
   * - When a snapshot does not exist, a bootstrap sequence is triggered using current operation and sessionLine.
   *
   * @param {string|number} sessionID Tab identifier used as snapshot key
   */
  const getSessionStorageData = (sessionID) => {
    ctx.setIsLoad(true);

    // Retrieves the snapshot payload for the requested tab identifier.
    const data = M_State.find((obj) => obj.key === sessionID)?.value;
    if (data) {
      const pick = (k) => data.find((x) => x.key === k)?.value;

      // Reads persisted values.
      const tempInfo = pick('laneInfo');
      const tempIF = pick('isFirst');
      const tempOP = pick('operation');
      const tempSL = pick('sessionLine');
      const tempSchema = pick('df.schema');

      // Applies persisted values to the active tab state.
      setLaneInfo(tempInfo || {});
      setIsFirst(tempIF ?? false);
      setOperation(tempOP ?? 'Error');
      setSessionLine(tempSL || {});
      if (Array.isArray(tempSchema)) setSchema(tempSchema);

      ctx.setIsLoad(false);
      return;
    }

    // Without a snapshot, performs a hydration chain based on current state.
    bootstrap(operation, sessionLine);
  };

  /* ------------------------------------------------------------------------------------------------------------
   * i18n handling
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Reacts to language changes.
   *
   * Responsibilities:
   * - Refresh Options to reflect translated labels or localized metadata when provided by the backend.
   * - Recompute the tab label using translated base labels.
   *
   * Notes:
   * - isFirst prevents running this effect during the initial mount bootstrap.
   */
  useEffect(() => {
    if (!operation || isFirst) return;

    fetchOptions();

    // Uses translated base labels for tab naming during language changes.
    const key =
      operation === 'Create'
        ? 'LabelNewRouteLaneRel'
        : operation === 'Edit'
          ? 'LabelEditRouteLane'
          : 'LabelViewRouteLane';

    const routeName = laneInfo?.Route ?? sessionLine?.Route ?? '';
    const laneName = laneInfo?.Lane ?? sessionLine?.Lane ?? '';
    const composed = routeName || laneName ? `${routeName} -> ${laneName}` : '';

    const base = t(key);
    const label =
      operation === 'Create' || !composed ? base : `${base} "${composed}"`;

    renameTab(String(selectedTab), label);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  /* ------------------------------------------------------------------------------------------------------------
   * Data loading: bootstrap, options, details
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Executes the hydration chain required for the screen.
   *
   * Flow:
   * - Enables the global loader.
   * - Fetches Options to render DynamicForm.
   * - If operation is Edit/View and composite keys exist, fetches entity details.
   * - Disables the global loader.
   *
   * @param {string} op Operation mode to use for deciding whether to fetch details
   * @param {object} line Navigation line containing composite identifiers
   * @returns {Promise<void>} Resolves when hydration finishes
   */
  const bootstrap = async (op, line) => {
    ctx.setIsLoad(true);
    try {
      await fetchOptions();

      const mode = (op || '').toLowerCase();
      const routeId = (line || sessionLine)?.RouteId;
      const laneId = (line || sessionLine)?.LaneId;

      // Details are loaded only when editing/viewing an existing composite record.
      if (mode !== 'create' && routeId && laneId) {
        await fetchDetails(routeId, laneId);
      }
    } finally {
      ctx.setIsLoad(false);
    }
  };

  /**
   * Loads Options metadata from the backend.
   *
   * Purpose:
   * - Provides the schema required by DynamicForm to render input controls.
   * - Keeps UI behavior aligned with server-side field definitions.
   *
   * Error handling:
   * - Failure payloads and exceptions are converted into a localized toast message.
   *
   * @returns {Promise<void>} Resolves after schema state is updated
   */
  const fetchOptions = async () => {
    try {
      const res = await svcRouteLane.options();
      if (isFailurePayload(res)) {
        toastMessages('error', '', apiMessage(res, t('ErrorLoadingData')));
        return;
      }
      setSchema(Array.isArray(res) ? res : []);
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  /**
   * Loads RouteLaneRel details using composite identifiers.
   *
   * Purpose:
   * - Provides the initial DynamicForm data values for Edit/View modes.
   *
   * Error handling:
   * - Failure payloads and exceptions are converted into a localized toast message.
   *
   * @param {number|string} routeId Route identifier
   * @param {number|string} laneId Lane identifier
   * @returns {Promise<void>} Resolves after laneInfo state is updated
   */
  const fetchDetails = async (routeId, laneId) => {
    try {
      const response = await makeCompositeService(routeId, laneId).get();
      if (isFailurePayload(response)) {
        toastMessages('error', '', apiMessage(response, t('ErrorLoadingData')));
        return;
      }
      setLaneInfo(response || {});
    } catch (err) {
      toastMessages('error', '', apiMessage(err, t('ErrorLoadingData')));
    }
  };

  /**
   * Refresh handler used by the toolbar.
   *
   * Purpose:
   * - Reloads Options schema for DynamicForm.
   * - Reloads details when composite keys exist.
   *
   * Behavior:
   * - Without composite keys, only Options are refreshed.
   * - With composite keys, Options and Details are refreshed in parallel.
   *
   * @param {object} line Optional navigation line overriding current sessionLine
   */
  const getRefresh = (line) => {
    const effective = line || sessionLine;
    const routeId = effective?.RouteId;
    const laneId = effective?.LaneId;

    // Without composite keys, details cannot be retrieved.
    if (!routeId || !laneId) {
      fetchOptions();
      return;
    }

    // Uses the global loader for page-level refresh operations.
    ctx.setIsLoad(true);
    Promise.all([fetchOptions(), fetchDetails(routeId, laneId)]).finally(() =>
      ctx.setIsLoad(false)
    );
  };

  /* ------------------------------------------------------------------------------------------------------------
   * DynamicForm integration
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Applies a partial values patch emitted by DynamicForm.
   *
   * Purpose:
   * - Keeps laneInfo synchronized with user edits without waiting for submit.
   * - Supports lightweight updates that improve tab label and snapshot persistence behavior.
   *
   * @param {object|null|undefined} patch Partial key/value patch from the form
   */
  const handleFormValuesChange = useCallback((patch) => {
    if (!patch) return;
    setLaneInfo((prev) => ({ ...prev, ...patch }));
  }, []);

  /**
   * Builds an API update payload from DynamicForm values using Options metadata.
   *
   * Purpose:
   * - Avoids hardcoding field mappings between UI and DTO.
   * - Converts Option field names into DTO keys by applying a standard lowerCamelCase rule.
   * - Normalizes booleans for payload consistency.
   *
   * Behavior:
   * - Reads schema entries and derives a form field key from Header or Name.
   * - Converts the form field key into a DTO key by lowercasing the first character.
   * - Copies values that are present in the submitted values object.
   *
   * @param {object} values DynamicForm submitted values
   * @returns {object} Payload object suitable for API update calls
   */
  const buildPayloadFromValues = useCallback(
    (values) => {
      const opts = Array.isArray(schema) ? schema : [];
      const entries = [];

      for (const opt of opts) {
        if (!opt) continue;

        // Determines which form key DynamicForm uses for this option.
        const formKey = opt.Header || opt.Name;
        if (!formKey) continue;

        // Converts schema field name into a DTO-style key.
        const dtoKey =
          String(formKey).charAt(0).toLowerCase() + String(formKey).slice(1);

        // Reads submitted value by the DynamicForm field key.
        let v = values?.[formKey];

        // Normalizes boolean values for consistent API payload shape.
        if (opt.Type === 'boolean' || opt.Type === 'bool') v = !!v;

        // Omits undefined values to avoid unintentionally overwriting fields.
        if (v !== undefined) entries.push([dtoKey, v]);
      }

      return Object.fromEntries(entries);
    },
    [schema]
  );

  /**
   * Submits DynamicForm values through the composite update endpoint.
   *
   * Purpose:
   * - Validates that composite keys exist for the relationship.
   * - Builds the payload from schema and form values.
   * - Persists the update through the backend.
   * - Notifies user feedback through toast messages.
   * - Closes the tab when the update succeeds.
   *
   * @param {object} values Form values provided by DynamicForm submit
   * @returns {Promise<void>} Resolves when submit flow completes
   */
  const onSubmitDynamic = useCallback(
    async (values) => {
      const routeId = sessionLine?.RouteId;
      const laneId = sessionLine?.LaneId;

      // Without composite keys, an update endpoint cannot be addressed.
      if (!routeId || !laneId) return;

      ctx.setIsLoad(true);
      try {
        // Converts DynamicForm values into an API payload aligned with Options schema.
        const payload = buildPayloadFromValues(values);

        // Submits the payload to the composite endpoint.
        const res = await makeCompositeService(routeId, laneId).put(payload);

        if (isFailurePayload(res)) {
          toastMessages('error', '', apiMessage(res, t('ErrorSaving')));
          return;
        }

        toastMessages('success', '', t('RouteLaneRelUpdated'));
        handleTabClose(id);
      } catch (err) {
        toastMessages('error', '', apiMessage(err, t('ErrorSaving')));
      } finally {
        ctx.setIsLoad(false);
      }
    },
    [
      sessionLine?.RouteId,
      sessionLine?.LaneId,
      id,
      t,
      toastMessages,
      ctx,
      makeCompositeService,
      handleTabClose,
      buildPayloadFromValues,
    ]
  );

  /**
   * Triggers the DynamicForm submission through the toolbar Save action.
   * The call is routed through the imperative formRef API.
   */
  const triggerFormSubmit = () => formRef.current?.submit?.();

  /* ------------------------------------------------------------------------------------------------------------
   * Map integration
   * ---------------------------------------------------------------------------------------------------------- */

  /**
   * Composite keys used by the map preview.
   * - routeId determines which lanes belong to the displayed route.
   * - currentLaneId is highlighted on top of the route lanes.
   */
  const routeId = sessionLine?.RouteId ?? null;
  const currentLaneId = sessionLine?.LaneId ?? null;

  /**
   * Stores local busy state for the map preview area.
   * RoutePreviewTable can report busy changes while it loads and resolves map layers.
   */
  const [mapBusy, setMapBusy] = useState(false);

  /**
   * Defines an optional relationship override list for RoutePreviewTable.
   *
   * Purpose:
   * - Allows the caller to provide a precomputed set of relationships when available.
   * - Leaves the preview component responsible for fetching when an override is not provided.
   *
   * Behavior:
   * - Returns undefined when routeId is not available so RoutePreviewTable can show its placeholder.
   * - Returns undefined when no relationship override is needed, allowing RoutePreviewTable to fetch normally.
   */
  const linkedLaneRelsOverride = useMemo(() => {
    // Without a route identifier, a relationship list cannot be scoped correctly.
    if (!routeId) return undefined;

    // Default behavior delegates relationship retrieval to RoutePreviewTable.
    return undefined;
  }, [routeId]);

  /* ------------------------------------------------------------------------------------------------------------
   * Render
   * ---------------------------------------------------------------------------------------------------------- */

  return (
    <>
      {/* Header area with toolbar actions. */}
      <MHeader
        element={
          <>
            {/* Refresh action reloads schema and details when keys are available. */}
            <MButton
              tooltip={t('RefreshTable')}
              icon={<i className="pi pi-refresh iconTable"></i>}
              onClick={() => getRefresh(sessionLine)}
              label={t('RefreshTable')}
            />

            {/* Save action is available only when the screen is not in view mode. */}
            {!isViewMode && (
              <MButton
                tooltip={t('Save')}
                icon={<i className="pi pi-save iconTable"></i>}
                onClick={triggerFormSubmit}
                label={t('Save')}
              />
            )}
          </>
        }
      />

      {/* Main content wrapper with consistent padding and background color. */}
      <div
        style={{
          padding: '20px',
          background: 'var(--color-white)',
        }}
      >
        <div className="firstDivContainer">
          {/* Layout container splitting the form and map into two panes. */}
          <div className="route-lane-config-layout">
            {/* Form pane: DynamicForm-driven editor. */}
            <div className="route-lane-config-form">
              <DynamicForm
                // Key ties the form instance to tab, operation, and composite identifiers.
                // This supports correct remounting behavior when navigating across different records.
                key={`df-routeLane-${selectedTab}-${operation}-${sessionLine?.RouteId ?? 'r'}-${sessionLine?.LaneId ?? 'l'}`}
                formRef={formRef}
                mode={(operation || 'View').toLowerCase()}
                schema={schema}
                data={laneInfo}
                onValuesChange={handleFormValuesChange}
                onSubmit={onSubmitDynamic}
                title={t('Route-Lane Information')}
                containerClassName="alignInputsUserForm"
                align="vertical"
              />
            </div>

            {/* Map pane: lane visualization for the selected route and lane. */}
            <div className="route-lane-config-map">
              <div
                style={{
                  height: "clamp(520px, 55vh, 850px)",
                  overflow: 'hidden',
                  position: 'relative',
                  boxSizing: 'border-box',
                  border: '2px solid var(--color-red-dark)',
                  borderRadius: 8,
                  background: 'var(--color-white)',
                }}
              >
                {/* Map preview reuses RoutePreviewTable and highlights the selected lane. */}
                <RoutePreviewTable
                  // When routeId is missing, the preview component shows its placeholder.
                  routeId={routeId ?? undefined}
                  linkedLaneRelsOverride={linkedLaneRelsOverride}
                  // Highlighting is driven by the composite LaneId when available.
                  highlightLaneId={currentLaneId ?? undefined}
                  highlightColor={COLOR_CURRENT}
                  highlightWidth={4}
                  // Embedded mode makes the preview fill the container and rely on the parent border.
                  fillHeight={true}
                  // Busy state is surfaced so this screen can show a local overlay.
                  onBusyChange={setMapBusy}
                />

                {/* Local overlay used to indicate map work without triggering the global loader. */}
                {mapBusy && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      zIndex: 10,
                      background: 'rgba(255,255,255,0.55)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      pointerEvents: 'none',
                      fontSize: 12,
                      color: 'rgba(0,0,0,0.70)',
                      fontWeight: 600,
                    }}
                    aria-label={t('Loading')}
                  >
                    {t('Loading')}…
                  </div>
                )}
              </div>

              {/* Guidance text shown when no route is selected and a preview cannot be generated. */}
              {!routeId && (
                <div style={{ marginTop: 10, fontSize: 12, opacity: 0.75 }}>
                  {t('SelectRouteToPreview')}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/**
 * Runtime prop validation for RouteLaneConfigCrud.
 *
 * Purpose:
 * - Documents expected props.
 * - Provides development-time warnings when invalid prop types are passed.
 */
RouteLaneConfigCrud.propTypes = {
  /**
   * Called to close the current tab after a successful save.
   */
  handleTabClose: PropTypes.func,

  /**
   * Displays toast notifications.
   * Expected signature typically includes severity, title, and message parameters.
   */
  toastMessages: PropTypes.func,
};
